import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Heart, Star, ShoppingBag, Zap, Truck, MapPin, Minus, Plus, RotateCcw, ShieldCheck, PackageCheck } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, inr, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";
import ProductCard from "../components/ProductCard";
import ImagePicker from "../components/ImagePicker";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { addToCart, toggleWishlist, wishlistIds, addRecent, fetchCart } = useShop();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [pincode, setPincode] = useState("");
  const [delivery, setDelivery] = useState(null);
  const [checkingPin, setCheckingPin] = useState(false);
  const [personal, setPersonal] = useState({ recipient_name: "", message: "", custom_text: "" });
  const [photo, setPhoto] = useState(null); // { data, name }
  const [reviews, setReviews] = useState({ reviews: [], distribution: {} });
  const [myRating, setMyRating] = useState(5);
  const [myText, setMyText] = useState("");
  const [tab, setTab] = useState("description");

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setImgIdx(0);
    setQty(1);
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setProduct(data);
        addRecent(data);
        const r = await api.get(`/products/${id}/reviews`);
        setReviews(r.data);
      } catch {
        setNotFound(true);
      }
    })();
  }, [id, addRecent]);

  const checkPincode = async (e) => {
    e?.preventDefault();
    if (!pincode.trim()) return;
    setCheckingPin(true);
    try {
      const { data } = await api.get("/delivery/estimate", { params: { pincode } });
      setDelivery(data);
    } catch {
      setDelivery({ serviceable: false, message: "Couldn't check delivery right now. Please try again." });
    } finally {
      setCheckingPin(false);
    }
  };

  const personalizationPayload = () => {
    if (!product?.personalized) return null;
    if (!personal.recipient_name.trim() && !personal.message.trim() && !personal.custom_text.trim() && !photo) return null;
    return { ...personal, photo_data: photo?.data || null, photo_name: photo?.name || null };
  };

  const handleAdd = async () => {
    try {
      await addToCart(product, qty, personalizationPayload());
      toast.success("Added to your cart", { action: { label: "View Cart", onClick: () => nav("/cart") } });
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };

  const handleBuyNow = async () => {
    try {
      await addToCart(product, qty, personalizationPayload());
      nav(user ? "/checkout" : "/auth", { state: { from: "/checkout" } });
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };

  const handleWish = async () => {
    const res = await toggleWishlist(product.id);
    if (res?.needsAuth) {
      toast.info("Sign in to save gifts you love");
      nav("/auth");
      return;
    }
    toast.success(res?.added ? "Saved to your wishlist" : "Removed from wishlist");
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) return nav("/auth");
    try {
      await api.post("/reviews", { product_id: product.id, rating: myRating, text: myText });
      toast.success("Thank you! Your review is live.");
      setMyText("");
      const r = await api.get(`/products/${id}/reviews`);
      setReviews(r.data);
      const { data } = await api.get(`/products/${id}`);
      setProduct(data);
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="product-not-found">
        <h1 className="font-serif text-3xl font-bold text-stone-900">This gift is currently unavailable.</h1>
        <p className="text-stone-500 mt-3">It may have sold out or been lovingly retired. Here are some similar gifts you may like.</p>
        <Link to="/shop" className="inline-block mt-8 px-8 py-3.5 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] transition-colors" data-testid="notfound-shop-link">Explore Gifts</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 grid lg:grid-cols-2 gap-12 animate-pulse">
        <div className="aspect-square rounded-3xl bg-[#F5EFE6]" />
        <div className="space-y-4 pt-6"><div className="h-8 bg-[#F5EFE6] rounded w-2/3" /><div className="h-5 bg-[#F5EFE6] rounded w-1/3" /><div className="h-24 bg-[#F5EFE6] rounded" /></div>
      </div>
    );
  }

  const price = product.effective_price ?? product.price;
  const flash = product.flash;
  const inWish = wishlistIds.includes(product.id);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 lg:py-14" data-testid="product-detail-page">
      <nav className="text-xs text-stone-400 mb-8">
        <Link to="/" className="hover:text-[#B45309]">Home</Link> / <Link to="/shop" className="hover:text-[#B45309]">Gifts</Link> / <span className="text-stone-600">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <motion.div key={imgIdx} initial={{ opacity: 0.4, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }} className="relative aspect-square rounded-3xl overflow-hidden bg-[#F5EFE6] border border-[#E9E2D7]">
            <img src={product.images[imgIdx]} alt={product.name} className="w-full h-full object-cover" data-testid="product-main-image" />
            {flash && <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-[#B45309] text-white text-xs font-bold uppercase tracking-wider shadow-lg">Flash Sale</span>}
          </motion.div>
          {product.images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setImgIdx(i)} className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${i === imgIdx ? "border-[#B45309]" : "border-transparent"}`} data-testid={`product-thumb-${i}`}>
                  <img src={img} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]">{product.category.replace(/-/g, " ")}</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 mt-2 tracking-tight" data-testid="product-name">{product.name}</h1>
          <div className="flex items-center gap-3 mt-3">
            <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-[#78866B]/15 text-[#5c6b52] text-sm font-bold"><Star size={13} fill="currentColor" /> {product.rating}</span>
            <span className="text-sm text-stone-500">{product.review_count} reviews</span>
            {product.stock <= 5 && product.stock > 0 && <span className="text-sm font-semibold text-[#B45309]">Only {product.stock} left in stock</span>}
          </div>

          <div className="flex items-baseline gap-3 mt-6">
            <span className="text-4xl font-bold text-stone-900" data-testid="product-price">{inr(price)}</span>
            {product.original_price > price && <span className="text-xl text-stone-400 line-through">{inr(product.original_price)}</span>}
            {product.original_price > price && <span className="px-2.5 py-1 rounded-full bg-[#B45309]/10 text-[#B45309] text-xs font-bold">{Math.round((1 - price / product.original_price) * 100)}% OFF</span>}
          </div>
          <p className="text-xs text-stone-400 mt-1">Inclusive of all taxes</p>

          {flash && (
            <div className="mt-4 p-4 rounded-2xl bg-[#F9F3EA] border border-[#E0A96D]/40">
              <p className="text-sm font-bold text-[#B45309] flex items-center gap-1.5"><Zap size={14} fill="currentColor" /> Flash Sale price — ends {new Date(flash.sale_end).toLocaleString("en-IN", { hour: "numeric", minute: "2-digit", day: "numeric", month: "short" })}</p>
              <div className="h-1.5 rounded-full bg-white mt-3 overflow-hidden"><div className="h-full bg-[#B45309] rounded-full" style={{ width: `${Math.min((flash.sold_qty / flash.max_qty) * 100, 100)}%` }} /></div>
              <p className="text-xs font-semibold text-stone-600 mt-1.5">{flash.sold_qty} / {flash.max_qty} sold · {flash.remaining <= 5 ? `Only ${flash.remaining} left at this price!` : "Selling fast!"}</p>
            </div>
          )}

          {product.personalized && (
            <div className="mt-6 p-5 rounded-2xl border-2 border-dashed border-[#E0A96D]/60 bg-[#FDFBF7]" data-testid="personalization-panel">
              <p className="text-sm font-bold text-stone-800 mb-3">Make it personal</p>
              <div className="space-y-3">
                <input value={personal.recipient_name} onChange={(e) => setPersonal({ ...personal, recipient_name: e.target.value })} placeholder="Recipient's name" className="w-full h-11 px-4 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-[#B45309]" data-testid="personalization-name" />
                <textarea value={personal.message} onChange={(e) => setPersonal({ ...personal, message: e.target.value })} placeholder="Your heartfelt message" rows={2} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-[#B45309] resize-none" data-testid="personalization-message" />
                <input value={personal.custom_text} onChange={(e) => setPersonal({ ...personal, custom_text: e.target.value })} placeholder="Custom text / engraving (optional)" className="w-full h-11 px-4 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-[#B45309]" data-testid="personalization-custom" />
                <div>
                  <p className="text-xs font-semibold text-stone-600 mb-2">Add a photo for printing (optional)</p>
                  <ImagePicker value={photo?.data || null} fileName={photo?.name} onChange={(data, name) => setPhoto(data ? { data, name } : null)} testId="personalization-photo" label="Select Image" />
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center border border-stone-300 rounded-full" data-testid="qty-selector">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-11 grid place-items-center text-stone-600 hover:text-[#B45309]" aria-label="Decrease quantity" data-testid="qty-minus"><Minus size={15} /></button>
              <span className="w-8 text-center font-bold text-stone-800">{qty}</span>
              <button onClick={() => setQty(Math.min(20, qty + 1))} className="w-10 h-11 grid place-items-center text-stone-600 hover:text-[#B45309]" aria-label="Increase quantity" data-testid="qty-plus"><Plus size={15} /></button>
            </div>
            <p className="text-sm text-stone-500">{product.stock > 0 ? `${product.stock} available` : "Out of stock"}</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button onClick={handleAdd} disabled={product.stock <= 0} className="flex-1 h-13 py-3.5 rounded-full border-2 border-stone-900 text-stone-900 text-sm font-bold flex items-center justify-center gap-2 hover:bg-stone-900 hover:text-white transition-colors disabled:opacity-50" data-testid="detail-add-cart">
              <ShoppingBag size={16} /> Add to Cart
            </button>
            <button onClick={handleBuyNow} disabled={product.stock <= 0} className="flex-1 py-3.5 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] hover:-translate-y-0.5 hover:shadow-lg transition-[background-color,transform,box-shadow] duration-300 disabled:opacity-50" data-testid="detail-buy-now">
              Buy Now
            </button>
            <button onClick={handleWish} aria-label="Wishlist" className={`h-13 w-13 px-3.5 rounded-full border-2 grid place-items-center transition-colors ${inWish ? "border-[#B45309] bg-[#B45309] text-white" : "border-stone-300 text-stone-600 hover:border-[#B45309] hover:text-[#B45309]"}`} data-testid="detail-wishlist">
              <Heart size={18} fill={inWish ? "currentColor" : "none"} />
            </button>
          </div>

          <form onSubmit={checkPincode} className="mt-8 p-5 rounded-2xl bg-white border border-[#E9E2D7]" data-testid="pincode-checker">
            <p className="text-sm font-bold text-stone-800 flex items-center gap-2"><MapPin size={15} className="text-[#B45309]" /> Check delivery</p>
            <div className="flex gap-2 mt-3">
              <input value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Enter pincode" inputMode="numeric" className="flex-1 h-11 px-4 rounded-xl border border-stone-200 text-sm outline-none focus:border-[#B45309]" data-testid="pincode-input" />
              <button disabled={checkingPin} className="h-11 px-5 rounded-xl bg-stone-900 text-white text-sm font-semibold hover:bg-[#B45309] transition-colors disabled:opacity-60" data-testid="pincode-check-btn">{checkingPin ? "…" : "Check"}</button>
            </div>
            {delivery && (
              <div className="mt-4" data-testid="pincode-result">
                {!delivery.serviceable ? (
                  <p className="text-sm text-red-700">{delivery.message || "Sorry, we don't deliver to this pincode yet."}</p>
                ) : (
                  <div className="space-y-2">
                    {delivery.options.map((o) => (
                      <p key={o.type} className="flex items-center gap-2 text-sm text-stone-700"><Truck size={14} className="text-[#78866B]" /> <b>{o.label}:</b> get it by {o.eta} <span className="text-stone-400">· {o.note}</span></p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      </div>

      {/* INFO TABS */}
      <div className="mt-16 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        <div className="flex lg:flex-col gap-2 overflow-x-auto no-scrollbar">
          {[["description", "Description"], ["highlights", "Highlights"], ["delivery", "Delivery & Returns"], ["reviews", `Reviews (${product.review_count})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`shrink-0 text-left px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${tab === k ? "bg-[#B45309]/10 text-[#B45309]" : "text-stone-500 hover:bg-[#F5EFE6]"}`} data-testid={`tab-${k}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-[#E9E2D7] p-6 sm:p-8 min-h-[200px]">
          {tab === "description" && <p className="text-stone-600 leading-relaxed">{product.description}</p>}
          {tab === "highlights" && (
            <ul className="space-y-3">
              {(product.highlights || []).map((h) => <li key={h} className="flex items-start gap-3 text-stone-600"><PackageCheck size={16} className="text-[#B45309] mt-0.5 shrink-0" /> {h}</li>)}
              <li className="flex items-start gap-3 text-stone-600"><ShieldCheck size={16} className="text-[#B45309] mt-0.5 shrink-0" /> Securely packed & quality checked</li>
            </ul>
          )}
          {tab === "delivery" && (
            <div className="space-y-4 text-stone-600 text-sm leading-relaxed">
              <p><b>Delivery:</b> Standard (3–5 days, free above ₹999), Express (1–2 days, ₹99) and Same Day (select metro pincodes, ₹149). Enter your pincode above for exact dates.</p>
              <p className="flex items-start gap-2"><RotateCcw size={16} className="text-[#B45309] mt-0.5 shrink-0" /> <span><b>Returns:</b> Damaged or incorrect items are replaced or refunded within 7 days. Fresh items (like flowers) are covered under our freshness promise — report within 24 hours of delivery. Personalized gifts are covered for damage or print defects.</span></p>
            </div>
          )}
          {tab === "reviews" && (
            <div data-testid="reviews-section">
              <div className="flex flex-wrap gap-8 items-start">
                <div className="text-center">
                  <p className="font-serif text-5xl font-bold text-stone-900">{product.rating}</p>
                  <div className="flex gap-0.5 justify-center mt-2">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={14} className={i < Math.round(product.rating) ? "text-[#E0A96D]" : "text-stone-300"} fill="currentColor" />)}</div>
                  <p className="text-xs text-stone-400 mt-1">{product.review_count} reviews</p>
                </div>
                {reviews.reviews.length > 0 && (
                  <div className="flex-1 min-w-[200px] space-y-1.5">
                    {[5, 4, 3, 2, 1].map((s) => {
                      const count = reviews.distribution[String(s)] || 0;
                      const pct = reviews.reviews.length ? (count / reviews.reviews.length) * 100 : 0;
                      return (
                        <div key={s} className="flex items-center gap-2 text-xs text-stone-500">
                          <span className="w-3">{s}★</span>
                          <div className="flex-1 h-1.5 rounded-full bg-[#F5EFE6] overflow-hidden"><div className="h-full bg-[#E0A96D] rounded-full" style={{ width: `${pct}%` }} /></div>
                          <span className="w-6 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="mt-8 space-y-5">
                {reviews.reviews.length === 0 && <p className="text-sm text-stone-400">No written reviews yet — be the first after your order arrives.</p>}
                {reviews.reviews.map((r) => (
                  <div key={r.id} className="border-b border-stone-100 pb-5">
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-full bg-[#E0A96D]/25 text-[#B45309] grid place-items-center text-sm font-bold">{r.reviewer?.[0]?.toUpperCase()}</span>
                      <div>
                        <p className="text-sm font-semibold text-stone-800">{r.reviewer}</p>
                        <p className="text-xs text-stone-400">{fmtDate(r.created_at)}</p>
                      </div>
                      <span className="ml-auto flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={12} className={i < r.rating ? "text-[#E0A96D]" : "text-stone-200"} fill="currentColor" />)}</span>
                    </div>
                    <p className="text-sm text-stone-600 mt-2">{r.text}</p>
                  </div>
                ))}
              </div>
              {user && (
                <form onSubmit={submitReview} className="mt-8 p-5 rounded-2xl bg-[#F9F3EA] border border-[#E9E2D7]" data-testid="review-form">
                  <p className="text-sm font-bold text-stone-800">Ordered this gift? Share your experience</p>
                  <div className="flex gap-1.5 mt-3">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button type="button" key={s} onClick={() => setMyRating(s)} aria-label={`Rate ${s}`} data-testid={`review-star-${s}`}><Star size={22} className={s <= myRating ? "text-[#E0A96D]" : "text-stone-300"} fill="currentColor" /></button>
                    ))}
                  </div>
                  <textarea value={myText} onChange={(e) => setMyText(e.target.value)} rows={3} placeholder="What did you love about it?" className="w-full mt-3 px-4 py-3 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-[#B45309] resize-none" data-testid="review-text" />
                  <button className="mt-3 h-10 px-6 rounded-full bg-stone-900 text-white text-sm font-semibold hover:bg-[#B45309] transition-colors" data-testid="review-submit">Submit Review</button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>

      {/* RELATED */}
      {product.related?.length > 0 && (
        <section className="mt-20" data-testid="related-products">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]">You May Also Like</p>
          <h2 className="font-serif text-3xl font-bold text-stone-900 mt-3 mb-8">Perfect gifts for you</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {product.related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
